// client/src/routes/app/contracts/$contractId/amendments/index.tsx — SCR-CON-003 tab
// „Acte adiționale" · CON-005 (04 §8.4, 05 CON-005 criterion 2).
//
// „Immutable timeline — amendments never edit in place" (04 §8.4). The one write affordance on
// this screen is „Act adițional nou", and it is in the HEADER above the list, never on a row:
// the way to change what an act did is another act. `AmendmentTimeline` renders no button and
// no link on any row at all — not disabled ones, none — and `contracts-screens.test.tsx` proves
// the absence rather than trusting it.
//
// The button is offered only where CON-001 would accept one: `contract-not-amendable` is a 422
// on anything but an ACTIVE contract, and a draft is edited directly instead. And only to a
// caller who may write the contract's flat (BR-COOWN-09 rule 5, D-2466): an act is a write on the
// contract, which is its apartment's. It falls away without a sentence of its own, because the
// contract's header — `ContractActions`, above every tab — already says who runs the flat.

import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { AmendmentTimeline } from '@/features/contracts/AmendmentTimeline';
import { useContract, useContractAmendments } from '@/features/contracts/queries';
import { usePropertyWrite } from '@/features/properties/administration';

export const Route = createFileRoute('/app/contracts/$contractId/amendments/')({
  component: AmendmentsTabScreen,
});

function AmendmentsTabScreen() {
  const { contractId } = Route.useParams();
  const { t } = useTranslation();
  const contract = useContract(contractId);
  const { canWrite } = usePropertyWrite(contract.data?.propertyId, 'Leasing.Write');
  const amendments = useContractAmendments(contractId);

  if (contract.isPending || amendments.isPending) return <LoadingState rows={3} />;
  if (contract.isError) return <ErrorState error={contract.error} onRetry={() => void contract.refetch()} />;
  if (amendments.isError) {
    return <ErrorState error={amendments.error} onRetry={() => void amendments.refetch()} />;
  }

  const amendable = canWrite && contract.data.status === 'ACTIVE';

  const action = amendable ? (
    <Link
      to="/app/contracts/$contractId/amendments/new"
      params={{ contractId }}
      className="btn btn-primary btn-sm"
    >
      <Icon name="document-text" className="h-4 w-4" />
      {t('contracts.amendments.add')}
    </Link>
  ) : undefined;

  return (
    <div className="space-y-4">
      {amendments.data.length > 0 && action ? <div className="flex justify-end">{action}</div> : null}
      <AmendmentTimeline amendments={amendments.data} action={action} />
    </div>
  );
}
